import type { SerializedXYWH } from '@blocksuite/global/utils';
import { Bound } from '@blocksuite/global/utils';

import type { MindmapElementModel } from '../../mindmap.js';

export const NODE_VERTICAL_SPACING = 45;
export const NODE_HORIZONTAL_SPACING = 110;
export const NODE_FIRST_LEVEL_HORIZONTAL_SPACING = 200;
export const BALANCE_SPACING_SCALE = 0.7;
export const VERTICAL_BRANCH_SPACING_SCALE = 0.4; // Tighter spacing for TOP/BOTTOM branches

export type NodeDetail = {
  /**
   * The index of the node, it decides the layout order of the node
   */
  index: string;
  parent?: string;

  /**
   * The preferred layout direction of the node, it only works when the layout type is BALANCE
   * and the node is on the first level
   */
  preferredDir?: LayoutType;

  /**
   * Optional image payload for node rendering.
   * If provided, renderers may draw the image instead of text.
   * Provide either an external/data URL or an inline SVG string.
   */
  image?: {
    url?: string;
    svg?: string;
  };

  /**
   * Node type determines how the node is rendered:
   * - 'text': Default, shows text only
   * - 'image': Shows image only, no text
   * - 'icon': Shows icon above text
   */
  nodeType?: 'text' | 'image' | 'icon';

  /**
   * Tags to display below the node text
   */
  tags?: Array<{ name: string; color?: string }>;

  /**
   * Whether the node's children are collapsed (hidden)
   */
  collapsed?: boolean;
};

export type MindmapNode = {
  id: string;
  detail: NodeDetail;

  element: BlockSuite.SurfaceElementModel;
  children: MindmapNode[];

  /**
   * This property override the preferredDir or default layout direction.
   * It is used during dragging that would temporary change the layout direction
   */
  overriddenDir?: LayoutType;
};

export type MindmapRoot = MindmapNode & {
  left: MindmapNode[];
  right: MindmapNode[];
};

export enum LayoutType {
  BALANCE = 2,
  LEFT = 1,
  RIGHT = 0,
}

type TreeSize = {
  /**
   * The root node of the tree
   */
  root: MindmapNode;

  /**
   * The size of the tree, including its descendants
   */
  bound: Bound;

  /**
   * The size of the children of the root
   */
  children: TreeSize[];
};

const calculateNodeSize = (
  root: MindmapNode,
  firstLevel = false,
  rootChildren?: MindmapNode[]
) => {
  const bound = root.element.elementBound;
  const children: TreeSize[] = [];

  if (root.detail.collapsed) {
    return { root, bound, children };
  }

  rootChildren = rootChildren ?? root.children;

  if (rootChildren) {
    const childrenBound = rootChildren.reduce(
      (pre, node) => {
        const childSize = calculateNodeSize(node);

        children.push(childSize);

        pre.w = Math.max(pre.w, childSize.bound.w);
        pre.h +=
          pre.h > 0
            ? NODE_VERTICAL_SPACING + childSize.bound.h
            : childSize.bound.h;

        return pre;
      },
      new Bound(0, 0, 0, 0)
    );

    bound.w +=
      childrenBound.w +
      (firstLevel
        ? NODE_FIRST_LEVEL_HORIZONTAL_SPACING
        : NODE_HORIZONTAL_SPACING);
    bound.h = Math.max(bound.h, childrenBound.h);
  }

  return {
    root,
    bound,
    children,
  };
};

const layoutTree = (
  tree: TreeSize,
  layoutType: LayoutType.LEFT | LayoutType.RIGHT,
  mindmap: MindmapElementModel,
  path: number[] = [0],
  children?: TreeSize[]
) => {
  const firstLevel = path.length === 1;
  const treeHeight = tree.bound.h;
  const baseSpacing = firstLevel
    ? NODE_FIRST_LEVEL_HORIZONTAL_SPACING
    : NODE_HORIZONTAL_SPACING;
  const spacing =
    mindmap.layoutType === LayoutType.BALANCE
      ? BALANCE_SPACING_SCALE * baseSpacing
      : baseSpacing;

  const currentX =
    layoutType === LayoutType.RIGHT
      ? tree.root.element.x + tree.root.element.w + spacing
      : tree.root.element.x - spacing;
  let currentY = tree.root.element.y + (tree.root.element.h - treeHeight) / 2;

  if (tree.root.element.h >= treeHeight && tree.children.length) {
    const onlyChild = tree.children[0];

    currentY += (tree.root.element.h - onlyChild.root.element.h) / 2;
  }

  tree.children.forEach((subtree, idx) => {
    const subtreeRootEl = subtree.root.element;
    const subtreeHeight = subtree.bound.h;
    const xywh = `[${layoutType === LayoutType.RIGHT ? currentX : currentX - subtreeRootEl.w
      },${currentY + (subtreeHeight - subtreeRootEl.h) / 2},${subtreeRootEl.w},${subtreeRootEl.h}]` as SerializedXYWH;

    idx = children ? children.indexOf(subtree) : idx;

    const currentNodePath = [...path, idx];

    subtreeRootEl.xywh = xywh;

    layoutTree(subtree, layoutType, mindmap, currentNodePath);

    currentY += subtreeHeight + NODE_VERTICAL_SPACING;
  });
};

const layoutRight = (
  root: MindmapNode,
  mindmap: MindmapElementModel,
  path = [0]
) => {
  const rootTree = calculateNodeSize(root, true);

  layoutTree(rootTree, LayoutType.RIGHT, mindmap, path);
};

const layoutLeft = (
  root: MindmapNode,
  mindmap: MindmapElementModel,
  path = [0]
) => {
  const rootTree = calculateNodeSize(root, true);

  layoutTree(rootTree, LayoutType.LEFT, mindmap, path);
};

/**
 * Calculate size for a subtree where first-level descendants are laid out horizontally
 * (used for TOP/BOTTOM sides in radial balance layout).
 */
const calculateNodeSizeHorizontal = (
  root: MindmapNode,
  firstLevel = false,
  rootChildren?: MindmapNode[]
) => {
  const bound = root.element.elementBound;
  const children: TreeSize[] = [];

  if (root.detail.collapsed) {
    return { root, bound, children };
  }

  const childrenNodes = rootChildren ?? root.children;

  if (childrenNodes && childrenNodes.length) {
    const childrenBound = childrenNodes.reduce(
      (pre, node) => {
        const childSize = calculateNodeSizeHorizontal(node);

        children.push(childSize);

        pre.h = Math.max(pre.h, childSize.bound.h);
        // Use tighter horizontal spacing for vertical branches
        const horizontalGap = NODE_VERTICAL_SPACING * 0.7;
        pre.w +=
          pre.w > 0
            ? horizontalGap + childSize.bound.w
            : childSize.bound.w;

        return pre;
      },
      new Bound(0, 0, 0, 0)
    );

    const verticalSpacing = firstLevel
      ? NODE_FIRST_LEVEL_HORIZONTAL_SPACING * VERTICAL_BRANCH_SPACING_SCALE
      : NODE_HORIZONTAL_SPACING * VERTICAL_BRANCH_SPACING_SCALE;

    bound.h += childrenBound.h + verticalSpacing;
    bound.w = Math.max(bound.w, childrenBound.w);
  }

  return {
    root,
    bound,
    children,
  };
};

/**
 * Lay out a subtree whose immediate children should be distributed horizontally,
 * positioned either above (TOP) or below (BOTTOM) the root.
 */
const layoutTreeVertical = (
  tree: TreeSize,
  verticalDir: 'TOP' | 'BOTTOM',
  mindmap: MindmapElementModel,
  path: number[] = [0],
  children?: TreeSize[]
) => {
  const firstLevel = path.length === 1;
  const treeWidth = tree.bound.w;
  const baseSpacing = firstLevel
    ? NODE_FIRST_LEVEL_HORIZONTAL_SPACING
    : NODE_HORIZONTAL_SPACING;
  // Use much tighter spacing for vertical branches to create circular look
  const spacing =
    mindmap.layoutType === LayoutType.BALANCE
      ? VERTICAL_BRANCH_SPACING_SCALE * baseSpacing
      : baseSpacing;

  const rootEl = tree.root.element;

  // Center the whole horizontal children band relative to the root
  let currentX = rootEl.x + (rootEl.w - treeWidth) / 2;

  // Determine the baseline Y for this band
  const baseY =
    verticalDir === 'BOTTOM'
      ? rootEl.y + rootEl.h + spacing
      : rootEl.y - spacing - tree.bound.h;

  // If single child and root is wider, nudge to center the only child
  if (rootEl.w >= treeWidth && tree.children.length === 1) {
    const onlyChild = tree.children[0];
    currentX += (rootEl.w - onlyChild.root.element.w) / 2;
  }

  tree.children.forEach((subtree, idx) => {
    const subtreeRootEl = subtree.root.element;
    const subtreeWidth = subtree.bound.w;

    const x =
      currentX + (subtreeWidth - subtreeRootEl.w) / 2;
    const y =
      baseY + (subtree.bound.h - subtreeRootEl.h) / 2;

    const xywh =
      `[${x},${y},${subtreeRootEl.w},${subtreeRootEl.h}]` as SerializedXYWH;

    idx = children ? children.indexOf(subtree) : idx;
    const currentNodePath = [...path, idx];

    subtreeRootEl.xywh = xywh;

    // Continue vertical layout for deeper levels on the same side
    layoutTreeVertical(subtree, verticalDir, mindmap, currentNodePath);

    // Use tighter horizontal spacing for siblings in vertical branches
    const horizontalGap = NODE_VERTICAL_SPACING * 0.7;
    currentX += subtreeWidth + horizontalGap;
  });
};

const layoutBalance = (
  root: MindmapNode,
  mindmap: MindmapElementModel,
  path = [0]
) => {
  // Build a radial distribution by splitting first-level children into
  // four buckets in round-robin order: RIGHT, BOTTOM, LEFT, TOP.
  const children = root.children ?? [];
  const right: MindmapNode[] = [];
  const bottom: MindmapNode[] = [];
  const left: MindmapNode[] = [];
  const top: MindmapNode[] = [];

  children.forEach((child, idx) => {
    const mod = idx % 4;
    if (mod === 0) right.push(child);
    else if (mod === 1) bottom.push(child);
    else if (mod === 2) left.push(child);
    else top.push(child);
  });

  // Reflect new LEFT/RIGHT buckets on root for downstream logic (e.g., getLayoutDir)
  (root as MindmapRoot).right = right.slice();
  (root as MindmapRoot).left = left.slice().reverse();

  // Use mock roots so we can reuse existing layout functions without mutating tree structure
  const rootTree = calculateNodeSize(root, true);

  // LEFT side (stacked vertically)
  if (left.length) {
    const leftTreeSize = calculateNodeSize(root, true, left);
    const mockRoot = {
      root: rootTree.root,
      bound: leftTreeSize.bound,
      children: leftTreeSize.children,
    };
    layoutTree(mockRoot, LayoutType.LEFT, mindmap, path, mockRoot.children);
  }

  // RIGHT side (stacked vertically)
  if (right.length) {
    const rightTreeSize = calculateNodeSize(root, true, right);
    const mockRoot = {
      root: rootTree.root,
      bound: rightTreeSize.bound,
      children: rightTreeSize.children,
    };
    layoutTree(mockRoot, LayoutType.RIGHT, mindmap, [0], mockRoot.children);
  }

  // TOP side (distributed horizontally)
  if (top.length) {
    const topTreeSize = calculateNodeSizeHorizontal(root, true, top);
    const mockRootTop = {
      root: rootTree.root,
      bound: topTreeSize.bound,
      children: topTreeSize.children,
    };
    layoutTreeVertical(mockRootTop, 'TOP', mindmap, [0], mockRootTop.children);
  }

  // BOTTOM side (distributed horizontally)
  if (bottom.length) {
    const bottomTreeSize = calculateNodeSizeHorizontal(root, true, bottom);
    const mockRootBottom = {
      root: rootTree.root,
      bound: bottomTreeSize.bound,
      children: bottomTreeSize.children,
    };
    layoutTreeVertical(
      mockRootBottom,
      'BOTTOM',
      mindmap,
      [0],
      mockRootBottom.children
    );
  }
};

export const layout = (
  root: MindmapNode,
  mindmap: MindmapElementModel,
  layoutDir: LayoutType | null,
  path: number[]
) => {
  layoutDir = layoutDir ?? mindmap.layoutType;

  switch (layoutDir) {
    case LayoutType.RIGHT:
      return layoutRight(root, mindmap, path);
    case LayoutType.LEFT:
      return layoutLeft(root, mindmap, path);
    case LayoutType.BALANCE:
      return layoutBalance(root, mindmap, path);
  }
};
